import { httpClient, sessionlessHttpClient } from '@/shared/api/httpClient'

/**
 * Result of /api/telegram/mini-app/auth endpoint
 */
export interface TelegramAuthResult {
    result: 'success' | 'totp_required' | 'error'
    /** Encrypted auth token (present on success or totp_required) */
    spa_auth_token?: string
    /** Telegram user info (legacy response) */
    telegram_user?: {
        id: number
        username?: string
    }
    /** Session ID for TOTP verification */
    session_id?: string
    /** User ID on success */
    user_id?: number
    /** Error message */
    message?: string
    /** Error code */
    code?: number
}

/**
 * Result of /api/telegram/mini-app/register endpoint
 */
export interface TelegramRegisterResult {
    result: 'success' | 'error'
    spa_auth_token?: string
    user_id?: number
    message?: string
    code?: number
}

/**
 * Result of /api/telegram/mini-app/verify-totp endpoint
 */
export interface TelegramVerifyTotpResult {
    result: 'success' | 'error'
    spa_auth_token?: string
    user_id?: number
    message?: string
}

/**
 * Result of /api/auth/exchange-token endpoint
 * API returns: { result: 'success', data: { success: true, token: '...' } }
 */
export interface ExchangeTokenResult {
    result: string
    data: {
        success: boolean
        token: string
    }
}

/**
 * Custom headers for Mini-App requests
 * Used by backend to enable sliding window for TOTP TTL refresh
 */
const MINI_APP_HEADERS = {
    'X-Telegram-Mini-App': '1',
}

/**
 * Service for Telegram Mini-App authentication.
 *
 * Handles the complete authentication lifecycle:
 * 1. Authenticate with initData → success / totp_required
 * 2. Verify TOTP code for 2FA users
 * 3. Exchange spa_auth_token for session hash
 *
 * @example
 * ```ts
 * import { TelegramAuthService } from '@/mini-app/TelegramAuthService'
 *
 * const result = await TelegramAuthService.authenticate(initData)
 *
 * if (result.result === 'success') {
 *   const token = await TelegramAuthService.exchangeToken(result.spa_auth_token!)
 *   localStorage.setItem('authToken', token)
 * }
 * ```
 */
export class TelegramAuthService {
    /**
     * Authenticate user via Telegram initData.
     *
     * Possible results:
     * - `success`: User found (or created on the spot) and authenticated, spa_auth_token provided
     * - `totp_required`: User has 2FA, need to verify TOTP code
     * - `error`: Authentication failed
     *
     * @param initData Raw initData string from Telegram WebApp
     * @returns Authentication result
     */
    static async authenticate(initData: string): Promise<TelegramAuthResult> {
        return sessionlessHttpClient.post<TelegramAuthResult>(
            '/telegram/mini-app/auth',
            { initData },
            { headers: MINI_APP_HEADERS },
        )
    }

    /**
     * Register new user with sponsor.
     *
     * Called when authenticate() returns sponsor_required.
     * Creates new user account linked to Telegram and sponsor.
     *
     * @param initData Raw initData string from Telegram WebApp
     * @param sponsorLogin Login of the sponsoring user
     * @returns Registration result with spa_auth_token on success
     */
    static async register(initData: string, sponsorLogin: string): Promise<TelegramRegisterResult> {
        return httpClient.post<TelegramRegisterResult>(
            '/telegram/mini-app/register',
            { initData, sponsorLogin },
            { headers: MINI_APP_HEADERS },
        )
    }

    /**
     * Verify TOTP code for 2FA authentication.
     *
     * Called when authenticate() returns totp_required.
     * Validates 6-digit code from authenticator app.
     *
     * Rate limiting: 5 attempts per 5 minutes, then blocked.
     *
     * @param spaAuthToken Token from authenticate() response
     * @param code 6-digit TOTP code
     * @returns Verification result with new spa_auth_token on success
     */
    static async verifyTotp(spaAuthToken: string, code: string): Promise<TelegramVerifyTotpResult> {
        return sessionlessHttpClient.post<TelegramVerifyTotpResult>(
            '/telegram/mini-app/verify-totp',
            { spa_auth_token: spaAuthToken, code },
            { headers: MINI_APP_HEADERS },
        )
    }

    /**
     * Exchange spa_auth_token for session token.
     *
     * The spa_auth_token is a one-time encrypted token (valid 60s).
     * This exchanges it for a permanent session token stored in localStorage.
     *
     * @param spaAuthToken Encrypted token from auth/register response
     * @returns Session token string to store in localStorage
     */
    static async exchangeToken(spaAuthToken: string): Promise<string> {
        const response = await sessionlessHttpClient.post<ExchangeTokenResult>(
            '/auth/exchange-token',
            { token: spaAuthToken },
            { headers: MINI_APP_HEADERS },
        )

        // API returns: { result: 'success', data: { success: true, token: '...' } }
        const token = response?.data?.token
        if (response?.result !== 'success' || response.data?.success !== true || typeof token !== 'string' || !token.trim()) {
            throw new Error('exchange-token: invalid response')
        }
        return token
    }
}
